import { useState, useRef, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { compose, tap, always } from 'ramda';

import { FloresDocument } from './view-documents.types';
import { selectSelectedCompany } from 'companies/companies.slice';
import { selectParticipant } from 'participant-search/participant.slice';
import request, { APIOptions } from 'shared/api-request';
import { addHttpErrorNotification } from 'notifications/notifications.slice';
import store from 'store';

export function useDocuments(isCobra: boolean) {
  const dispatch = useDispatch<typeof store.dispatch>();

  const [ documents, setDocuments ] = useState<FloresDocument[] | null>(null);

  const { compid } = useSelector(selectSelectedCompany) ?? { compid: undefined };
  const { empno } = useSelector(selectParticipant) ?? { empno: undefined };

  const { current: abortCtrl } = useRef(new AbortController());
  const { current: setDefault } = useRef(compose(setDocuments, always([])));
  const { current: handleError } = useRef(compose(
    tap(setDefault),
    dispatch,
    addHttpErrorNotification,
  ));
  const mountedRef = useRef(false);

  useEffect(() => {
    mountedRef.current = true;
    const uri = `/api/companies/${compid}/qualifying-events/${isCobra ? 'c' : 'r'}/documents/${empno}`;
    const apiOpts: APIOptions = { method: 'GET', signal: abortCtrl.signal };

    request<FloresDocument[]>(uri, apiOpts)
      .then((result) => {
        if (mountedRef.current) {
          setDocuments(result ?? []);
        }
      })
      .catch((err) => {
        if (mountedRef.current) {
          handleError(err);
        }
      });

    return () => {
      mountedRef.current = false;
      abortCtrl.abort();
    };
  }, [ dispatch, compid, empno, setDefault, handleError, abortCtrl, isCobra ]);

  return documents;
}
